import { useEffect, useMemo, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useAuth, useUser } from "@clerk/react";
import { AlertTriangleIcon, MessageCircleQuestionIcon, RefreshCwIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FrameDescription,
  FramePanel,
  FrameFooter,
} from "@/components/ui/frame";
import { useCampaignStore } from "@/stores";
import { useContractTemplatesStore } from "@/stores/eve/contractTemplatesStore";
import { useAmyDeepLink } from "@/hooks/use-amy-deep-link";
import type { EvePhasePatch, EvePhaseSettings, EvePipelinePhase } from "@/types/eve";

/**
 * Contract phase → template picker + field-by-field prefill mapping, saved
 * through the SAME update-campaign-phases route as the rest of the Pipeline
 * tab (the chat tool's validated executor: unassigned/archived/multi-signer
 * templates and malformed entries are refused server-side). Rows come from
 * the selected template's prefillable fields (list-contract-templates);
 * each row maps to a stored value, a CRM field, fixed text, or an `extract`
 * instruction Amy resolves per creator from the negotiation thread at
 * contract time. `prefill` always replaces the whole mapping on save.
 */

type PrefillEntry = NonNullable<EvePhaseSettings["prefill"]>[number];

/** Per-field editor state — absent key = the field stays blank on the paper. */
interface MappingValue {
  source: string;
  static_value?: string;
  instructions?: string;
}

const BLANK = "__blank__";

const BUILTIN_SOURCES: { group: string; options: { value: string; label: string }[] }[] = [
  {
    group: "Creator & deal",
    options: [
      { value: "fullname", label: "Creator full name" },
      { value: "username", label: "Creator username" },
      { value: "email", label: "Creator email" },
      { value: "negotiated_price", label: "Agreed price" },
      { value: "promo_code", label: "Promo code" },
      { value: "unique_link", label: "Tracking link" },
      { value: "offer", label: "Current tier's offer text" },
      { value: "offer_value", label: "Current tier's offer amount" },
    ],
  },
  {
    group: "Campaign",
    options: [
      { value: "campaign_name", label: "Campaign name" },
      { value: "number_of_deliverables", label: "Number of deliverables (campaign setting)" },
      { value: "today", label: "Today's date" },
    ],
  },
];

const BUILTIN_SOURCE_VALUES = new Set(BUILTIN_SOURCES.flatMap((g) => g.options.map((o) => o.value)));

function toMapping(prefill: PrefillEntry[]): Record<string, MappingValue> {
  const out: Record<string, MappingValue> = {};
  for (const e of prefill) {
    if (!e.docuseal_field || !e.source) continue;
    out[e.docuseal_field] = {
      source: e.source,
      ...(e.static_value !== undefined ? { static_value: e.static_value } : {}),
      ...(e.instructions !== undefined ? { instructions: e.instructions } : {}),
    };
  }
  return out;
}

/** Entries in a stable order: template fields first (template order), then mapped fields the template lacks. */
function toPrefill(mapping: Record<string, MappingValue>, rowOrder: string[]): PrefillEntry[] {
  const out: PrefillEntry[] = [];
  for (const field of rowOrder) {
    const m = mapping[field];
    if (!m) continue;
    out.push({
      docuseal_field: field,
      source: m.source,
      ...(m.source === "static" ? { static_value: m.static_value ?? "" } : {}),
      ...(m.source === "extract" ? { instructions: m.instructions ?? "" } : {}),
    });
  }
  return out;
}

function serialize(prefill: PrefillEntry[]): string {
  return JSON.stringify(
    prefill.map((e) => [e.docuseal_field, e.source, e.static_value ?? "", e.instructions ?? ""])
  );
}

export function ContractTemplateEditor({
  phase,
  save,
  disabled,
}: {
  phase: EvePipelinePhase;
  /** PhaseSection's patch saver (update-campaign-phases with token/org resolved). */
  save: (patches: EvePhasePatch[]) => Promise<boolean>;
  disabled: boolean;
}) {
  const { campaign, campaignId } = useCampaignStore();
  const { getToken } = useAuth();
  const { user } = useUser();
  const openAmy = useAmyDeepLink();
  const store = useContractTemplatesStore();

  // Picking/changing the template is Uplodio-staff only (Clerk publicMetadata
  // uplodio_member — read client-side so the picker doesn't pop in after the
  // fetch; the save route re-verifies). Members map the fields.
  const canPickTemplate =
    Boolean((user?.publicMetadata as Record<string, unknown> | undefined)?.uplodio_member) ||
    (store.campaignId === campaignId && store.canPickTemplate);

  const serverTemplateId = phase.settings?.template_id ?? null;
  const serverPrefill = useMemo(() => phase.settings?.prefill ?? [], [phase.settings?.prefill]);
  const serverKey = serialize(serverPrefill);

  const [templateId, setTemplateId] = useState<number | null>(serverTemplateId);
  useEffect(() => setTemplateId(serverTemplateId), [serverTemplateId]);
  const [mapping, setMapping] = useState<Record<string, MappingValue>>(() => toMapping(serverPrefill));
  // Re-seed only when the SERVER mapping changes (a save elsewhere never clobbers edits here).
  useEffect(() => setMapping(toMapping(serverPrefill)), [serverKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Templates + the chosen template's fields; re-read when the pick changes.
  const fetchTemplates = store.fetchTemplates;
  useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;
    void getToken().then((token) => {
      if (!token || cancelled) return;
      void fetchTemplates(campaignId, templateId, token);
    });
    return () => {
      cancelled = true;
    };
  }, [campaignId, templateId, getToken, fetchTemplates]);

  const forThisCampaign = store.campaignId === campaignId;
  const templates = forThisCampaign ? store.templates : [];
  const selected = forThisCampaign && store.selected?.id === templateId ? store.selected : null;
  const selectedError = forThisCampaign ? store.selectedError : null;
  const crmFields = (campaign?.fields ?? []).map((f) => f.name).filter((n) => n.trim() !== "");

  // Rows: the template's fields in template order, then mapped fields the template doesn't have.
  const templateFieldNames = selected?.fields.map((f) => f.name) ?? [];
  const extraFields = Object.keys(mapping).filter((f) => !templateFieldNames.includes(f));
  const rowOrder = [...templateFieldNames, ...extraFields];

  const draftPrefill = toPrefill(mapping, rowOrder);
  const prefillDirty = serialize(draftPrefill) !== serverKey;
  const templateDirty = templateId !== serverTemplateId;
  const dirty = prefillDirty || templateDirty;

  const invalid = draftPrefill.find(
    (e) => (e.source === "static" && !e.static_value?.trim()) || (e.source === "extract" && !e.instructions?.trim())
  );

  const setRow = (field: string, patch: Partial<MappingValue> | null) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (patch === null) {
        delete next[field];
      } else {
        next[field] = { ...(prev[field] ?? { source: "" }), ...patch };
      }
      return next;
    });
  };

  const handleSourceChange = (field: string, value: string) => {
    if (value === BLANK) return setRow(field, null);
    const prev = mapping[field];
    setRow(field, {
      source: value,
      static_value: value === "static" ? (prev?.static_value ?? "") : undefined,
      instructions: value === "extract" ? (prev?.instructions ?? "") : undefined,
    });
  };

  const handleCancel = () => {
    setTemplateId(serverTemplateId);
    setMapping(toMapping(serverPrefill));
  };

  const handleSave = () => {
    const settings: EvePhasePatch["settings"] = {};
    if (templateDirty && templateId != null) settings.template_id = templateId;
    if (prefillDirty) settings.prefill = draftPrefill;
    if (Object.keys(settings).length === 0) return;
    void save([{ key: phase.key, settings }]);
  };

  const busy = disabled || store.isLoading;
  const templateLabel = (id: number) => templates.find((t) => t.id === id)?.name ?? selected?.name ?? `Template #${id}`;

  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>Contract template</FrameTitle>
        <FrameDescription>The DocuSeal template Amy sends and how its fields are filled.</FrameDescription>
      </FrameHeader>

      {/* Panel 1: the template. Each mapped field below is its own panel, so
          the frame's native panel spacing separates them. */}
      <FramePanel className="flex flex-col gap-3 text-sm">
        {store.loadError && forThisCampaign ? (
          <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
            <AlertTriangleIcon className="size-4 shrink-0 text-warning" />
            <span>{store.loadError}</span>
            <Button
              variant="secondary"
              size="sm"
              className="ml-auto"
              disabled={busy}
              onClick={() => {
                if (!campaignId) return;
                void getToken().then((token) => {
                  if (token) void fetchTemplates(campaignId, templateId, token);
                });
              }}
            >
              <RefreshCwIcon /> Retry
            </Button>
          </div>
        ) : !canPickTemplate ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className={templateId != null ? "" : "text-muted-foreground"}>
              {templateId != null ? templateLabel(templateId) : "No template set yet."}
            </span>
            <span className="text-xs text-muted-foreground">
              {templateId != null
                ? "— the template is managed by Uplodio; map its fields below."
                : "Your Uplodio contact sets the DocuSeal template; the contract phase can't be enabled without one."}
            </span>
            {store.isLoading && <Spinner className="size-4 shrink-0 text-muted-foreground" />}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Select
              value={templateId != null ? String(templateId) : ""}
              onValueChange={(v) => setTemplateId(Number(v))}
              disabled={busy || (templates.length === 0 && templateId == null)}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    store.isLoading && !store.loaded
                      ? "Loading templates…"
                      : templates.length === 0
                        ? "No DocuSeal templates available"
                        : "Pick a template"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name || `Template #${t.id}`}
                  </SelectItem>
                ))}
                {templateId != null && !templates.some((t) => t.id === templateId) && (
                  <SelectItem value={String(templateId)}>{templateLabel(templateId)}</SelectItem>
                )}
              </SelectContent>
            </Select>
            {store.isLoading && <Spinner className="size-4 shrink-0 text-muted-foreground" />}
          </div>
        )}

        {store.loaded && store.shared && !store.loadError && canPickTemplate && (
          <span className="text-xs text-muted-foreground">
            Uplodio's shared DocuSeal account — only templates in your organization's folder are listed.
          </span>
        )}

        {selectedError && (
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <AlertTriangleIcon className="mt-0.5 size-3.5 shrink-0 text-warning" />
            <span>{selectedError}</span>
          </div>
        )}

        {templateId == null && !store.loadError && canPickTemplate && (
          <p className="text-muted-foreground">
            Pick a template to map its fields. The contract phase can't be enabled without one.
          </p>
        )}
      </FramePanel>

      {/* The mapping: one compact row per template field (template order,
          then mapped fields the template no longer has), label left, the
          control filling the remaining width, rows split by a divider. */}
      {templateId != null && rowOrder.length > 0 && (
        <FramePanel className="flex flex-col text-sm">
          <div className="grid grid-cols-[minmax(0,13rem)_minmax(0,1fr)] gap-x-6 pb-2 text-[10px] uppercase tracking-wide text-muted-foreground/70">
            <span>In the template</span>
            <span>Filled from</span>
          </div>
          <div className="divide-y divide-dashed">
            {rowOrder.map((field) => {
              const tf = selected?.fields.find((f) => f.name === field);
              const value = mapping[field];
              const source = value?.source ?? BLANK;
              const isCustomSource =
                value && !BUILTIN_SOURCE_VALUES.has(value.source) && !["static", "extract"].includes(value.source) && !crmFields.includes(value.source);
              return (
                <div key={field} className="grid grid-cols-[minmax(0,13rem)_minmax(0,1fr)] items-start gap-x-6 py-3 first:pt-1 last:pb-0">
                  <div className="flex min-h-9 flex-col justify-center gap-0.5">
                    <Badge variant="muted" size="sm" className="font-mono">{field}</Badge>
                    {tf ? (
                      <span className="text-[10px] text-muted-foreground/60">
                        {tf.type}
                        {tf.required ? " · required" : ""}
                      </span>
                    ) : selected ? (
                      <span className="text-[10px] text-warning">not on this template</span>
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Select value={source} onValueChange={(v) => handleSourceChange(field, v)} disabled={busy}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={BLANK}>Leave blank</SelectItem>
                        <SelectSeparator />
                        {BUILTIN_SOURCES.map((g) => (
                          <SelectGroup key={g.group}>
                            <SelectLabel>{g.group}</SelectLabel>
                            {g.options.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                        {crmFields.length > 0 && (
                          <SelectGroup>
                            <SelectLabel>CRM fields</SelectLabel>
                            {crmFields.map((name) => (
                              <SelectItem key={name} value={name}>
                                {name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        )}
                        {isCustomSource && (
                          <SelectGroup>
                            <SelectLabel>Current value</SelectLabel>
                            <SelectItem value={value.source}>{value.source} (CRM field)</SelectItem>
                          </SelectGroup>
                        )}
                        <SelectSeparator />
                        <SelectGroup>
                          <SelectLabel>Other</SelectLabel>
                          <SelectItem value="static">Fixed text…</SelectItem>
                          <SelectItem value="extract">Amy reads it from the thread…</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    {source === "static" && (
                      <Input
                        placeholder="The exact text to print"
                        maxLength={1000}
                        value={value?.static_value ?? ""}
                        onChange={(e) => setRow(field, { static_value: e.target.value })}
                        disabled={busy}
                      />
                    )}
                    {source === "extract" && (
                      <>
                        <Textarea
                          className="min-h-16 text-sm"
                          placeholder="What Amy should read out of the conversation — e.g. the deliverables the creator agreed to, as one sentence"
                          maxLength={1000}
                          value={value?.instructions ?? ""}
                          onChange={(e) => setRow(field, { instructions: e.target.value })}
                          disabled={busy}
                        />
                        <span className="text-[11px] text-muted-foreground/70">
                          Read once per creator from the thread, CRM values and brief when the contract is
                          prepared. If the conversation doesn't settle it, the creator is handed to you instead
                          of printing a blank.
                        </span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </FramePanel>
      )}

      <FrameFooter className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            openAmy(
              `Update the contract template or prefill mapping for campaign "${campaign?.campaign_name ?? ""}" (campaign_id ${campaignId}).`
            )
          }
        >
          <MessageCircleQuestionIcon /> Ask Amy instead
        </Button>
        {dirty && invalid && (
          <span className="ml-auto text-xs text-muted-foreground">
            "{invalid.docuseal_field}" needs {invalid.source === "static" ? "the fixed text" : "instructions for Amy"}.
          </span>
        )}
        {dirty && (
          <>
            <Button
              size="sm"
              variant="secondary"
              className={`${invalid ? "" : "ml-auto "}animate-in fade-in-0 slide-in-from-right-4 duration-200`}
              disabled={busy}
              onClick={handleCancel}
              aria-label="Discard contract template changes"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="animate-in fade-in-0 slide-in-from-right-2 duration-200"
              disabled={busy || Boolean(invalid)}
              onClick={handleSave}
            >
              Save Contract
            </Button>
          </>
        )}
      </FrameFooter>
    </Frame>
  );
}
